// Top-up catalogue (spec/01 §16.2) — pure: no DB, env or server imports, so the payment server, the
// pricing page and unit tests share one definition. `payments.server.ts` re-exports everything here.

export type Currency = 'IDR' | 'USD'
export type AddonKind = 'prd' | 'chat' | 'runner' | 'reviewer' | 'parallel'
export const ADDON_KINDS: AddonKind[] = ['prd', 'chat', 'runner', 'reviewer', 'parallel']
/**
 * What checkout sells (CONTRACT-E6 §8, lead decision): `parallel` is NOT for sale in E6 — a boost above 4 needs a wider
 * wave / protocol v1 change (WorkflowIntegration.checks max 4, ≤ 4 tasks per wave, cloud RUNNER_MAX_CONCURRENT).
 * Its settlement, credits and entitlement logic stay (ADDON_KINDS) for an owner grant or a later release.
 */
export type AddonKindForSale = Exclude<AddonKind, 'parallel'>
export const ADDON_KINDS_FOR_SALE: AddonKindForSale[] = ['prd', 'chat', 'runner', 'reviewer']
export const addonForSale = (kind: string): kind is AddonKindForSale => (ADDON_KINDS_FOR_SALE as string[]).includes(kind)

/** `addon_credit.kind` written on settlement (Runner reads `runner_tasks`; Orkestrator E6: `reviewer_runs`, `parallel_boost`). */
export const ADDON_CREDIT_KIND: Record<AddonKind, string> = { prd: 'prd', chat: 'chat', runner: 'runner_tasks', reviewer: 'reviewer_runs', parallel: 'parallel_boost' }

export interface AddonPrice {
  price: number
  /** units granted per purchase (credits; for `parallel` = extra concurrent agents) */
  credits: number
  /** days until the credit expires (`addon_credit.expires_at`); absent = never expires */
  days?: number
}

/** Fixed add-on prices (credits are consumed after the plan quota). */
export const ADDONS: Record<AddonKind, Record<Currency, AddonPrice>> = {
  // Repriced 4 Sep 2026 per docs/PRICING_MODEL.md (chat top-up was sold below cost).
  prd: { IDR: { price: 39_000, credits: 5 }, USD: { price: 3, credits: 5 } },
  chat: { IDR: { price: 89_000, credits: 100 }, USD: { price: 6, credits: 100 } },
  // Audit 9 Sep 2026 (PLAN_AUDIT_BISNIS_NONTECH.md A4): +10 verified Runner steps; ≈ Rp 4.000 real per step → 46 % margin.
  runner: { IDR: { price: 99_000, credits: 10 }, USD: { price: 7, credits: 10 } },
  // Orkestrator E6 (CONTRACT-E6 §1/§8): +25 Pemeriksa (reviewer agent) sessions, spent after the monthly plan quota, never expire.
  // 25 × ≈ Rp 1.611 (0.4 × a step, cloud worst case) → ≈ 49 % margin at Rp 79.000 (docs/PRICING_MODEL.md).
  reviewer: { IDR: { price: 79_000, credits: 25 }, USD: { price: 5, credits: 25 } },
  // Orkestrator E6: +2 concurrent agents on top of the plan's parallelRuns/serveParallel for 30 days from payment — NOT for sale (ADDON_KINDS_FOR_SALE).
  parallel: { IDR: { price: 59_000, credits: 2, days: 30 }, USD: { price: 4, credits: 2, days: 30 } },
}

/** Pseudo-slug used for the Paddle price mapping: PADDLE_PRICE_ADDON_<KIND>. */
export const addonSlug = (kind: AddonKind) => `addon-${kind}`

/** `expires_at` for a purchase settled at `now` (null = never). */
export function addonExpiry(kind: AddonKind, currency: Currency, now = new Date()): Date | null {
  const days = ADDONS[kind][currency].days
  return days ? new Date(now.getTime() + days * 86_400_000) : null
}

/** Human item name for the gateway / transaction list (kept identical for the pre-E6 kinds). */
export function addonItemName(kind: AddonKind, a: AddonPrice): string {
  if (kind === 'reviewer') return `${a.credits} Pemeriksa reviews`
  if (kind === 'parallel') return `+${a.credits} parallel agents (${a.days ?? 30} days)`
  return `${a.credits} ${kind === 'prd' ? 'PRD' : kind === 'chat' ? 'chat' : 'Runner step'} credits`
}

/** order_id = BS-<ts36>-<rand>[-PRD|-CHAT|-RUNNER|-REVIEWER|-PARALLEL] (add-ons carry their kind in the id since transaction has no addon column). */
export function newOrderId(addon?: AddonKind) {
  const id = `BS-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`
  return addon ? `${id}-${addon.toUpperCase()}` : id
}
const ADDON_SUFFIX = new RegExp(`-(${ADDON_KINDS.map((k) => k.toUpperCase()).join('|')})$`)
export function orderAddon(orderId: string): AddonKind | null {
  const m = ADDON_SUFFIX.exec(orderId)
  return m ? (m[1]!.toLowerCase() as AddonKind) : null
}

/** Guided (Phase 3): `…-REVIEW` orders are fulfilled by `guided.server`, not by a plan or an add-on (`-REVIEWER` is the Pemeriksa top-up). */
export const REVIEW_ORDER_SUFFIX = 'REVIEW'
export const orderIsReview = (orderId: string) => new RegExp(`-${REVIEW_ORDER_SUFFIX}$`).test(orderId)

/** One `addon_credit` row as far as the totals care. */
export interface CreditRowLike { kind: string; remaining: number; expiresAt: Date | null }
/**
 * Spendable top-up totals (settings "Kredit add-on", `subscriptionStatus().addonCredits`): only rows with
 * `remaining > 0` that have not expired count. `parallelBoost` = concurrent agents added by active boosts
 * (an expired 30-day boost adds nothing); `reviewerCredits` = unspent Pemeriksa top-up sessions.
 */
export function creditTotals(rows: CreditRowLike[], now = new Date()) {
  const sum = (kind: string) => rows.filter((r) => r.kind === kind && r.remaining > 0 && (!r.expiresAt || r.expiresAt > now)).reduce((a, r) => a + r.remaining, 0)
  return { prdCredits: sum('prd'), chatCredits: sum('chat'), coachingCredits: sum('coaching'), reviewerCredits: sum(ADDON_CREDIT_KIND.reviewer), parallelBoost: sum(ADDON_CREDIT_KIND.parallel) }
}
